import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  AlertCircle,
  Plus,
  Check,
  Clock,
  Settings,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
} from "lucide-react";
import Calendar from "./Calendar";
import { usePolls } from "../hooks/usePolls";
import { usePoll } from "../hooks/usePoll";
import { PollCreatorService } from "../services/PollCreatorService";
import {
  TimeSlotsTable,
  GranularitySelector,
  AdvancedConfigurationPanel,
} from "./time-slots";
import { logger } from "../lib/logger";
import type { Poll } from "../lib/pollStorage";
import {
  convertGeminiSlotsToTimeSlotsByDate,
  calculateOptimalGranularity,
} from "../services/TimeSlotConverter";
import type {} from "../services/PollCreatorService";
import {
  type PollCreationState,
  type TimeSlot,
} from "../services/PollCreatorService";
import type { DatePollSuggestion } from "../lib/gemini";
import { useAuth } from "../contexts/AuthContext";
import { UserMenu } from "./UserMenu";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import { VoteGrid } from "./voting/VoteGrid";
import { groupConsecutiveDates } from "../lib/date-utils";
import PollSettingsForm from "./polls/PollSettingsForm";
// MIGRATION Phase 5: Type migré vers apps/date-polls/src/types/
// import type { DatePollSettings } from "@core/types/products/date-polls";
import { SafeguardSection } from "./polls/SafeguardSection";
import { sendDatePollInvitation } from "../services/EmailService";
import { PendingPollService } from "../services/PendingPollService";
import { usePendingPollRestore } from "../hooks/usePendingPollRestore";
import { PendingPollRestoreModal } from "./polls/PendingPollRestoreModal";
import PrePublishModalContainer from "./poll-creator/pre-publish/PrePublishModalContainer";
import type { SettingKey } from "./poll-creator/pre-publish/types";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import RecurrenceSection, {
  type RecurrenceConfig,
} from "./poll-creator/RecurrenceSection";
import { generateDatesFromRule, RecurrenceError } from "../lib/recurrence";
import { useFreemiumQuota } from "../hooks/useFreemiumQuota";
import { supabase } from "../lib/supabase";

interface PollCreatorProps {
  onBack?: () => void;
  onOpenMenu?: () => void;
  onFinalize?: (draft?: any, poll?: any) => void;
  initialData?: DatePollSuggestion;
  withBackground?: boolean;
  onShare?: () => void;
}

const PollCreator: React.FC<PollCreatorProps> = ({
  initialData,
  onFinalize,
  onShare,
}: PollCreatorProps) => {
  const { t } = useTranslation(["creator", "common", "errors"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    createPoll,
    updatePoll,
    loading: pollLoading,
    error: pollError,
  } = usePolls();
  const [createdPollSlug, setCreatedPollSlug] = useState<string | null>(null);
  // Id serveur du sondage déjà créé/édité dans cette session. Survit aux
  // re-rendus de la même instance. Permet à un 2e clic "créer le sondage"
  // (après création OU modification) de faire un UPDATE au lieu d'un INSERT,
  // ce qui empêche la duplication. Pour l'édition via deep-link, c'est
  // editPollId (depuis l'URL) qui joue ce rôle au remontage.
  const persistedPollIdRef = useRef<string | null>(null);

  // Hook pour la restauration des sondages en attente
  const { hasPendingPoll, pendingPoll, isRestoring, restorePoll, dismissPoll } =
    usePendingPollRestore();

  // 📜 Doctrine pricing (27/03/2026) : participants et réponses par sondage
  //    sont illimités pour ANONYMOUS / FREE / SOLO (cf. constants/quotas.ts
  //    "participants: unlimited" + Docs/QUOTAS&PRICING/-Pricing.md:143
  //    "Réponses illimitées (vs 25 max)" comme argument vs SurveyMonkey).
  //    Aucun cap basé sur le plan n'est appliqué ici.

  // États pour le panneau configuration
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedSettings, setAdvancedSettings] = useState<any>({
    showLogo: true,
    requireAuth: false,
    oneResponsePerPerson: false,
    allowEditAfterSubmit: true,
    sendEmailCopy: false,
    emailForCopy: "",
    sendNotifications: false,
    resultsVisibility: "public",
  });

  // Pre-publish modal (Pattern B du handoff Settings Surfacing)
  const { flags } = useFeatureFlags();
  const enablePrePublishModal = flags.enablePrePublishModal ?? false;
  const [showPrePublishModal, setShowPrePublishModal] = useState(false);

  // Sondage récurrent (SOLO). Fréquence non-custom → les dates du calendrier
  // sont générées depuis la règle (voir effet plus bas) ; custom → le
  // calendrier reste la source des dates.
  const [recurrence, setRecurrence] = useState<RecurrenceConfig | null>(null);
  const { status: freemiumStatus } = useFreemiumQuota();
  const hasPaidPlan = freemiumStatus.groups.hasPaidPlan;

  /** Applique une valeur suggérée du `SettingHint` au state. */
  const applyPrePublishSuggestion = useCallback(
    (key: SettingKey, value: unknown) => {
      setAdvancedSettings((prev: any) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Récupérer l'ID du sondage à éditer depuis l'URL
  const urlParams = new URLSearchParams(window.location.search);
  const editPollId = urlParams.get("edit");

  // Récupérer les données d'initialisation depuis l'URL pour les tests E2E
  const urlTitle = urlParams.get("title");
  const urlDates = urlParams.get("dates");

  // Hook pour récupérer les données du sondage à éditer depuis Supabase
  const {
    data: pollToEdit,
    isLoading: isLoadingPoll,
    error: editPollError,
  } = usePoll(editPollId || "", {
    enabled: !!editPollId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // État de chargement pour l'édition
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  // État de soumission pour protéger contre les doubles clics
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Garde synchrone : isSubmitting (state) n'est pas à jour dans la closure
  // sur des clics rapides successifs, ce qui laissait passer plusieurs
  // createPoll() en parallèle → plusieurs sondages créés.
  const isSubmittingRef = useRef(false);

  // Créer les données initiales si présentes dans l'URL
  const urlInitialData =
    urlTitle || urlDates
      ? {
          title: urlTitle || undefined,
          dates: urlDates ? [urlDates] : undefined,
        }
      : undefined;
  const { toast } = useToast();
  const shareRef = useRef<HTMLDivElement>(null);
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  const timeGridRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const canFinalize = () => PollCreatorService.canFinalize(state);
  const handleFinalize = async () => {
    // Protéger contre les doubles soumissions (garde synchrone via ref +
    // garde anti-doublon si un sondage a déjà été créé dans cette session).
    if (isSubmittingRef.current || createdPollSlug) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const parsedEmails = Array.isArray(state.participantEmails)
        ? state.participantEmails
        : state.participantEmails
            .split(/[,;\n]+/)
            .map((email) => email.trim())
            .filter(Boolean);

      // Mode de partage : déterminé par les settings, avec fallback inféré
      // pour les anciens flux qui ne settent pas explicitement audienceMode.
      const audienceMode: "group" | "emails" | "link" =
        advancedSettings.audienceMode ??
        (advancedSettings.audienceGroupId
          ? "group"
          : parsedEmails.length > 0
            ? "emails"
            : "link");

      // En mode "link", on ne propage aucun email (pas d'envoi).
      const invitedEmails = audienceMode === "link" ? [] : parsedEmails;

      // Cible d'update : un sondage déjà créé dans cette session (ref) OU
      // un sondage ouvert en édition via ?edit=<id>. Si présent → UPDATE
      // (jamais de doublon). Sinon → INSERT.
      const targetPollId = persistedPollIdRef.current || editPollId;

      // Settings partagés entre create et update. SOURCE UNIQUE :
      // PollCreatorService.buildPollSettings (cf. son commentaire). Tout champ
      // de settings s'ajoute là-bas, pas ici, pour ne pas re-diverger.
      // audienceMode est spécifique à ce chemin (création réelle) → ajouté ici.
      const pollSettings = {
        ...PollCreatorService.buildPollSettings(state, advancedSettings),
        audienceMode,
        // Flag denormalisé pour le badge dashboard (évite une requête série
        // par carte) ; la source de vérité reste recurring_series.
        ...(recurrence && !targetPollId ? { isRecurring: true } : {}),
      };

      const result = targetPollId
        ? await updatePoll(targetPollId, {
            type: "date",
            title: state.pollTitle,
            description: null,
            selectedDates: state.selectedDates,
            timeSlotsByDate: timeSlotsByDate,
            invitedEmails: invitedEmails,
            settings: pollSettings,
          })
        : await createPoll({
            type: "date",
            title: state.pollTitle,
            description: null,
            selectedDates: state.selectedDates,
            timeSlotsByDate: timeSlotsByDate,
            participantEmails: invitedEmails,
            invitedEmails: invitedEmails, // NOUVEAU: Stocker les emails invités pour les rappels
            settings: pollSettings,
          });
      if (result.poll) {
        // Mémoriser l'id pour qu'un éventuel ré-enregistrement update au lieu
        // de dupliquer.
        persistedPollIdRef.current = result.poll.id;
        setCreatedPollSlug(result.poll.slug);

        // Sondage récurrent : créer la série + instances (création seulement,
        // pas en update — une série existante n'est pas régénérée).
        if (recurrence && !targetPollId) {
          const finalRule =
            recurrence.rule.freq === "custom"
              ? {
                  freq: "custom" as const,
                  dates: [...state.selectedDates].sort(),
                }
              : recurrence.rule;
          const { error: seriesError } = await supabase.rpc(
            "create_recurring_series",
            {
              p_poll_id: result.poll.id,
              p_recurrence_rule: finalRule,
              p_reminder_days_before: recurrence.reminderDaysBefore,
              p_reminder_timezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            },
          );
          if (seriesError) {
            logger.error("Recurring series creation failed", "poll", {
              error: seriesError,
            });
            toast({
              title: t("creator:recurrence.series_failed_title"),
              description: t("creator:recurrence.series_failed_description"),
              variant: "destructive",
            });
          } else {
            toast({
              title: t("creator:recurrence.series_created_title"),
              description: t("creator:recurrence.series_created_description", {
                count: state.selectedDates.length,
              }),
            });
          }
        }

        const groupName: string | undefined =
          advancedSettings.audienceGroupName;

        // Audience info attachée au poll pour piloter l'écran de succès.
        let audienceResult: {
          mode: "group" | "emails" | "link";
          sentCount: number;
          groupName?: string;
          emailDelivered: boolean;
        } = {
          mode: audienceMode,
          sentCount: 0,
          groupName,
          emailDelivered: audienceMode === "link",
        };

        // Auto-envoi pour les modes "group" et "emails".
        if (audienceMode !== "link" && invitedEmails.length > 0) {
          try {
            const invitationResult = await sendDatePollInvitation({
              pollId: result.poll.id,
              pollTitle: result.poll.title,
              pollUrl: `${window.location.origin}/poll/${result.poll.slug}`,
              recipientEmails: invitedEmails,
              senderName:
                user?.user_metadata?.name || user?.email || "DooDates",
            });

            audienceResult = {
              mode: audienceMode,
              sentCount: invitationResult.sentCount,
              groupName,
              emailDelivered: invitationResult.success,
            };

            if (invitationResult.success) {
              toast({
                title:
                  audienceMode === "group" && groupName
                    ? t("creator:invitations.sent_to_group", { groupName })
                    : t("creator:invitations.sent_title"),
                description:
                  audienceMode === "group" && groupName
                    ? t("creator:invitations.sent_to_group_description", {
                        count: invitationResult.sentCount,
                        groupName,
                      })
                    : t("creator:invitations.sent_description", {
                        count: invitationResult.sentCount,
                      }),
              });
            } else {
              toast({
                title: t("creator:poll_created.title"),
                description: t("creator:invitations.not_sent_description"),
                variant: "destructive",
              });
            }
          } catch (error) {
            audienceResult.emailDelivered = false;
            toast({
              title: t("creator:poll_created.title"),
              description: t("creator:invitations.send_error_description"),
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: t("creator:poll_created.title"),
            description: t("creator:poll_created.share_description", {
              title: result.poll.title,
            }),
          });
        }

        // Appeler onFinalize pour communiquer avec AICreationWorkspace.
        // On attache l'info d'audience au poll renvoyé pour piloter le modal
        // de succès sans modifier la signature consommée par 4 appelants.
        if (onFinalize) {
          const pollWithAudience = {
            ...result.poll,
            __audience: audienceResult,
          } as any;
          onFinalize(null, pollWithAudience);
        }

        // Mettre à jour la conversation si le sondage a été créé depuis le chat
        // Utiliser la fonction partagée pour garantir la cohérence
        // TODO: Get conversation ID from context and pass proper parameters
        // linkPollToConversationBidirectional(conversationId, result.poll.id, "date");
      } else {
        // Bug #7 — surface createPoll errors instead of silently failing.
        // Particulièrement important en mode guest : sans feedback, le user
        // croit que rien ne s'est passé et recommence en boucle.
        logger.error("Poll creation failed", "poll", {
          error: result.error,
        });
        toast({
          title: t("creator:create_failed.title"),
          description: result.error || t("creator:create_failed.description"),
          variant: "destructive",
        });
      }
    } catch (error) {
      logger.error("Error creating poll", "poll", error);
      // Bug #7 — ne pas laisser l'utilisateur devant un écran muet
      toast({
        title: t("creator:create_failed.title"),
        description:
          error instanceof Error
            ? error.message
            : t("creator:create_failed.description"),
        variant: "destructive",
      });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };
  const toggleDate = (dateString: string) =>
    PollCreatorService.toggleDate(dateString, setState);

  const handleEmailInput = (emailString: string) => {
    setState((prev) => ({
      ...prev,
      participantEmails: emailString,
      emailErrors: PollCreatorService.validateEmails(emailString),
    }));
  };

  const handleTimeSlotToggle = (
    dateStr: string,
    hour: number,
    minute: number,
  ) => {
    console.log(
      " [POLL_CREATOR] handleTimeSlotToggle:",
      dateStr,
      hour + ":" + minute,
      "mergeMode:",
      state.mergeMode,
    );

    // Utiliser le service partagé pour éviter la duplication de code
    const newTimeSlotsByDate = PollCreatorService.handleTimeSlotToggle(
      dateStr,
      hour,
      minute,
      timeSlotsByDate,
      state.timeGranularity,
      true, // includeDuration = true pour PollCreator
      state.mergeMode,
    );

    console.log(
      " [POLL_CREATOR] result:",
      "enabledSlots:",
      newTimeSlotsByDate[dateStr]?.filter((s) => s.enabled).length || 0,
      "mergeMode:",
      state.mergeMode,
    );

    setTimeSlotsByDate(newTimeSlotsByDate);
  };

  const getTimeSlotBlocks = (dateStr: string) => {
    const blocks = PollCreatorService.getTimeSlotBlocks(
      timeSlotsByDate[dateStr] || [],
      state.timeGranularity,
      state.mergeMode,
    );

    console.log(
      " [POLL_CREATOR] getTimeSlotBlocks:",
      `date=${dateStr} totalSlots=${timeSlotsByDate[dateStr]?.length || 0} enabledSlots=${timeSlotsByDate[dateStr]?.filter((s) => s.enabled).length || 0} blocksCount=${blocks.length} mergeMode=${state.mergeMode}`,
      blocks
        .map(
          (b) =>
            `block[${b.start.hour}:${b.start.minute.toString().padStart(2, "0")}-${b.end.hour}:${b.end.minute.toString().padStart(2, "0")}]`,
        )
        .join(" "),
    );

    return blocks;
  };

  // Charger les données du sondage à éditer depuis Supabase
  useEffect(() => {
    if (!editPollId) {
      if (!initialData) {
        localStorage.removeItem("doodates-draft");
      }
      setIsLoadingEdit(false);
      return;
    }

    // Activer le chargement au début
    setIsLoadingEdit(true);

    // Si les données du sondage sont chargées depuis Supabase
    if (pollToEdit) {
      try {
        // Nettoyer le draft avant de charger les données
        localStorage.removeItem("doodates-draft");

        // Extraire les dates depuis les données du sondage (UnifiedPoll format)
        let pollDates: string[] = [];

        // Méthode 1: Depuis selectedDates (format UnifiedPoll)
        if (
          (pollToEdit as any).selectedDates &&
          (pollToEdit as any).selectedDates.length > 0
        ) {
          pollDates = [...(pollToEdit as any).selectedDates];
        }

        // Méthode 2: Depuis settings.selectedDates (compatibilité)
        if (
          pollDates.length === 0 &&
          (pollToEdit as any).settings?.selectedDates?.length > 0
        ) {
          pollDates = [...(pollToEdit as any).settings.selectedDates];
        }

        // Méthode 3: Depuis les options du sondage (compatibilité ancien format)
        if (pollDates.length === 0 && (pollToEdit as any).options) {
          (pollToEdit as any).options.forEach(
            (option: { option_date?: string }) => {
              if (
                option.option_date &&
                !pollDates.includes(option.option_date)
              ) {
                pollDates.push(option.option_date);
              }
            },
          );
        }

        // Méthode 4: Fallback - générer des dates par défaut
        if (pollDates.length === 0) {
          const today = new Date();
          for (let i = 0; i < 3; i++) {
            const futureDate = new Date(today);
            futureDate.setDate(today.getDate() + i + 1);
            pollDates.push(futureDate.toISOString().split("T")[0]);
          }
        }

        // Réinitialiser complètement l'état avec toutes les propriétés requises
        const newState = {
          pollTitle: pollToEdit.title || "",
          selectedDates: pollDates,
          currentMonth: pollDates[0] ? new Date(pollDates[0]) : new Date(),
          showTimeSlots:
            (pollToEdit as any).showTimeSlots ||
            (pollToEdit as any).settings?.showTimeSlots ||
            false,
          participantEmails:
            (pollToEdit as any).participantEmails ||
            (pollToEdit as any).settings?.participantEmails ||
            "",
          calendarConnected: false,
          timeSlots: [],
          notificationsEnabled:
            (pollToEdit as any).notificationsEnabled || false,
          userEmail: "",
          showCalendarConnect: false,
          showShare: false,
          showDescription: false,
          emailErrors: [],
          showExtendedHours: false,
          timeGranularity: (pollToEdit.settings as any)?.timeGranularity || 60,
          showGranularitySettings: false,
          pollLinkCopied: false,
          expirationDays: 30,
          showExpirationSettings: false,
          showCalendarConnection: false,
          showSettingsPanel: false,
          allowMaybeVotes:
            (pollToEdit.settings as any)?.allowMaybeVotes || false,
          allowAnonymousVotes:
            (pollToEdit.settings as any)?.allowAnonymousVotes || false,
          // Restaurer le mode fusion depuis les settings persistés pour ne pas
          // perdre le regroupement à la ré-édition d'un sondage fusionné.
          mergeMode: (pollToEdit.settings as any)?.mergeMode === true,
        };

        setState(newState);

        // Charger les créneaux horaires si disponibles
        const timeSlotsData =
          (pollToEdit as any).timeSlotsByDate ||
          (pollToEdit.settings as any)?.timeSlotsByDate;
        if (timeSlotsData) {
          setTimeSlotsByDate(timeSlotsData);

          // Activer l'affichage des créneaux horaires si des créneaux existent
          const hasTimeSlots = Object.values(timeSlotsData).some(
            (
              slots: Array<{
                hour: number;
                minute: number;
                enabled: boolean;
              }>,
            ) => slots && slots.length > 0,
          );
          if (hasTimeSlots) {
            newState.showTimeSlots = true;
          }
        } else {
          setTimeSlotsByDate({});
        }

        logger.info("Poll data loaded from Supabase", "poll", {
          pollId: editPollId,
          title: pollToEdit.title,
          datesCount: pollDates.length,
        });
      } catch (error) {
        logger.error("Error loading poll data from Supabase", "poll", error);
      }
    }

    // Gérer les erreurs de chargement
    if (editPollError) {
      logger.error("Failed to load poll from Supabase", "poll", editPollError);
      toast({
        title: t("creator:load_error.title"),
        description: t("errors:poll_load_failed"),
        variant: "destructive",
      });
      setIsLoadingEdit(false);
    } else if (pollToEdit) {
      // Données chargées avec succès
      setIsLoadingEdit(false);
    }
  }, [editPollId, pollToEdit, editPollError, toast, t]);

  const [state, setState] = useState<PollCreationState>(
    PollCreatorService.initializeWithGeminiData(
      initialData || urlInitialData
        ? {
            title: (initialData || urlInitialData).title,
            dates: (initialData || urlInitialData).dates,
          }
        : undefined,
    ) as PollCreationState,
  );

  const handleGranularityChange = (value: number) => {
    setState((prev) => ({
      ...prev,
      timeGranularity: value,
      needsTimeSlotRecalculation: true,
    }));
  };

  // Règle de récurrence non-custom valide → injecte les occurrences générées
  // dans le calendrier (state.selectedDates), le reste du flux (horaires,
  // finalisation) fonctionne alors sans changement.
  useEffect(() => {
    if (!recurrence || recurrence.rule.freq === "custom") return;
    if (!recurrence.rule.dtstart || !recurrence.rule.until) return;
    try {
      const dates = generateDatesFromRule(recurrence.rule);
      setState((prev) => {
        if (JSON.stringify(prev.selectedDates) === JSON.stringify(dates)) {
          return prev;
        }
        return { ...prev, selectedDates: dates };
      });
    } catch (err) {
      // Règle invalide (trop/pas assez d'occurrences) : la section affiche
      // déjà l'erreur, on ne touche pas aux dates.
      if (!(err instanceof RecurrenceError)) throw err;
    }
  }, [recurrence]);

  const getVisibleTimeSlots = useCallback(() => {
    return PollCreatorService.getVisibleTimeSlots(
      state.timeSlots,
      state.showExtendedHours,
    );
  }, [state.timeSlots, state.showExtendedHours]);

  // Régénérer les timeSlots quand la granularité change
  useEffect(() => {
    // Régénérer seulement les timeSlots de base, PAS les timeSlotsByDate
    const newTimeSlots = PollCreatorService.initializeTimeSlots(
      state.timeGranularity,
    );
    setState((prev) => ({
      ...prev,
      timeSlots: newTimeSlots,
    }));
  }, [state.timeGranularity]);
  const [visibleMonths, setVisibleMonths] = useState<Date[]>([]);
  const [timeSlotsByDate, setTimeSlotsByDate] = useState<
    Record<string, TimeSlot[]>
  >({});
  const [compactDateHeaders, setCompactDateHeaders] = useState(false);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  // Ref pour la table unifiée
  const unifiedTableRef = useRef<HTMLDivElement>(null);

  // Initialize visible months on mount
  useEffect(() => {
    if (visibleMonths.length === 0) {
      // Si on a des dates initiales, commencer par le mois de la première date
      let startDate = new Date();

      if (initialData?.dates && initialData.dates.length > 0) {
        const firstDate = new Date(initialData.dates[0]);
        if (!isNaN(firstDate.getTime())) {
          startDate = firstDate;
        }
      }

      const months: Date[] = [];
      for (let i = 0; i < 6; i++) {
        const month = new Date(
          startDate.getFullYear(),
          startDate.getMonth() + i,
          1,
        );
        months.push(month);
      }
      setVisibleMonths(months);
    }
  }, [initialData, visibleMonths.length]);

  // Effet pour détecter quand les en-têtes de dates doivent être en mode compact
  useEffect(() => {
    const dateCount = state.selectedDates.length;
    // Passer en mode compact s'il y a plus de 8 dates (seuil ajustable)
    setCompactDateHeaders(dateCount > 8);
  }, [state.selectedDates.length]);

  // Effet pour s'assurer que les données initiales sont bien transmises à l'état
  useEffect(() => {
    if (initialData) {
      setState((prev) => {
        const updates: Partial<PollCreationState> = {};

        // Mettre à jour les dates si nécessaire
        if (
          initialData.dates &&
          initialData.dates.length > 0 &&
          JSON.stringify(prev.selectedDates) !==
            JSON.stringify(initialData.dates)
        ) {
          updates.selectedDates = initialData.dates;
          updates.showTimeSlots = true;
        }

        // FIX: Mettre à jour le titre s'il est présent et différent
        if (initialData.title && prev.pollTitle !== initialData.title) {
          updates.pollTitle = initialData.title;
        }

        // FIX: Mettre à jour les participants s'ils sont présents et différents
        if (initialData.participants && initialData.participants.length > 0) {
          const emailsStr = initialData.participants.join(", ");
          if (prev.participantEmails !== emailsStr) {
            updates.participantEmails = emailsStr;
          }
        }

        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    }
  }, [initialData]);

  useEffect(() => {
    // FIX: Lift declaration to avoid ReferenceError
    let convertedTimeSlots: Record<string, TimeSlot[]> = {};
    let optimalGranularity = 30; // Default

    console.log("🚀 [PollCreator] Initializing time slots", {
      hasTimeSlots: !!initialData?.timeSlots,
      hasTimeSlotsByDate: !!initialData?.timeSlotsByDate,
      timeSlotsByDate: initialData?.timeSlotsByDate,
      dates: initialData?.dates,
    });

    if (initialData?.timeSlotsByDate) {
      console.log(
        "✅ [PollCreator] Using initialData.timeSlotsByDate",
        initialData.timeSlotsByDate,
      );
      convertedTimeSlots = initialData.timeSlotsByDate as Record<
        string,
        TimeSlot[]
      >;
      optimalGranularity = calculateOptimalGranularity(convertedTimeSlots);
    } else if (initialData?.timeSlots && initialData.timeSlots.length > 0) {
      console.log(
        "🔄 [PollCreator] Converting initialData.timeSlots",
        initialData.timeSlots,
      );

      // FIX: Calculate optimal granularity from RAW slots first if possible
      // (calculateOptimalGranularity currently expects InternalTimeSlot[] though)
      // So we do a first pass with 15min to find the best granularity
      const tempSlots = convertGeminiSlotsToTimeSlotsByDate(
        initialData.timeSlots,
        initialData.dates || [],
        15, // Smallest possible to detect any offset
      );
      optimalGranularity = calculateOptimalGranularity(tempSlots);
      console.log(
        "📏 [PollCreator] Detected optimal granularity:",
        optimalGranularity,
      );

      // Second pass with the REAL granularity to align with the grid
      convertedTimeSlots = convertGeminiSlotsToTimeSlotsByDate(
        initialData.timeSlots,
        initialData.dates || [],
        optimalGranularity,
      );
    }

    if (Object.keys(convertedTimeSlots).length > 0) {
      console.log(
        "📊 [PollCreator] Final convertedTimeSlots",
        convertedTimeSlots,
      );
      setTimeSlotsByDate(convertedTimeSlots);

      // Ouvrir automatiquement le panneau de précision des horaires
      setState((prev) => ({
        ...prev,
        showTimeSlots: true,
        showGranularitySettings: false, // Menu fermé par défaut
        timeGranularity: optimalGranularity,
      }));
    } else {
      console.log("⚠️ [PollCreator] No time slots found after conversion");
      return;
    }

    // Trouver la première heure sélectionnée pour scroller la grille
    const firstSlot = Object.values(convertedTimeSlots)[0]?.[0];
    const firstHour = firstSlot?.hour || 0;

    // Faire défiler vers la section horaires après un délai suffisant
    setTimeout(() => {
      if (timeSlotsRef.current) {
        const scrollContainer =
          timeSlotsRef.current.closest(".overflow-y-auto");

        if (scrollContainer) {
          const elementTop = timeSlotsRef.current.offsetTop;
          const elementHeight = timeSlotsRef.current.offsetHeight;
          const containerHeight = scrollContainer.clientHeight;
          const scrollTo = elementTop - containerHeight / 2 + elementHeight / 2;

          scrollContainer.scrollTo({
            top: Math.max(0, scrollTo),
            behavior: "smooth",
          });
        }

        // Scroller aussi la grille horaire vers la première heure sélectionnée
        setTimeout(() => {
          if (unifiedTableRef.current && firstHour > 0) {
            const targetRow = unifiedTableRef.current.querySelector(
              `[data-hour="${firstHour}"]`,
            );

            if (targetRow) {
              targetRow.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        }, 300);
      }
    }, 600);
  }, [initialData]);

  // Effet pour recalculer les timeSlots quand la granularité change
  useEffect(() => {
    console.log("🔍 DEBUG - Granularité change:", {
      needsRecalculation: state.needsTimeSlotRecalculation,
      timeSlotsByDate: state.timeSlotsByDate,
      timeGranularity: state.timeGranularity,
      hasTimeSlots:
        state.timeSlotsByDate && Object.keys(state.timeSlotsByDate).length > 0,
      timeSlotsCount: state.timeSlotsByDate
        ? Object.keys(state.timeSlotsByDate).length
        : 0,
    });

    // Si recalcul nécessaire et on a soit des timeSlotsByDate soit des timeSlots par défaut
    if (
      state.needsTimeSlotRecalculation &&
      ((state.timeSlotsByDate &&
        Object.keys(state.timeSlotsByDate).length > 0) ||
        (state.timeSlots && state.timeSlots.length > 0))
    ) {
      const recalculateSlots = async () => {
        try {
          console.log(
            "🔄 Début du recalcul avec granularité:",
            state.timeGranularity,
          );

          let newTimeSlotsByDate = {};

          // Si on a déjà des timeSlotsByDate, les recalculer
          if (
            state.timeSlotsByDate &&
            Object.keys(state.timeSlotsByDate).length > 0
          ) {
            newTimeSlotsByDate =
              await PollCreatorService.recalculateTimeSlotsWithGranularity(
                state.timeSlotsByDate,
                state.timeGranularity,
              );
          }
          // Sinon, ne rien générer (pas de créneaux par défaut)
          else {
            newTimeSlotsByDate = {};
          }

          setTimeSlotsByDate(newTimeSlotsByDate);

          // Reset the flag
          setState((prev) => ({
            ...prev,
            needsTimeSlotRecalculation: false,
          }));
        } catch (error) {
          console.error("❌ Erreur lors du recalcul des créneaux:", error);
          // Reset flag even on error to avoid infinite loop
          setState((prev) => ({
            ...prev,
            needsTimeSlotRecalculation: false,
          }));
        }
      };

      recalculateSlots();
    } else {
      console.log("⏭️ Pas de recalcul nécessaire - conditions non remplies");
    }
  }, [
    state.needsTimeSlotRecalculation,
    state.timeGranularity,
    state.timeSlotsByDate,
    state.timeSlots,
    state.selectedDates,
  ]);

  // Effet pour faire défiler automatiquement vers le premier mois sélectionné
  useEffect(() => {
    const datesToCheck = initialData?.dates || state.selectedDates;

    if (
      datesToCheck.length > 0 &&
      visibleMonths.length > 0 &&
      calendarRef.current
    ) {
      const firstSelectedDate = new Date(datesToCheck[0]);
      const targetMonthIndex = visibleMonths.findIndex(
        (month) =>
          month.getFullYear() === firstSelectedDate.getFullYear() &&
          month.getMonth() === firstSelectedDate.getMonth(),
      );

      if (targetMonthIndex > 0) {
        setTimeout(() => {
          const calendarContainer =
            calendarRef.current?.querySelector(".overflow-x-auto");
          if (calendarContainer) {
            const monthWidth = 320;
            const gap = 16;
            const scrollPosition = targetMonthIndex * (monthWidth + gap);
            calendarContainer.scrollTo({
              left: scrollPosition,
              behavior: "smooth",
            });
          }
        }, 100);
      }
    }
  }, [initialData?.dates, state.selectedDates, visibleMonths]);

  const copyPollLink = async () => {
    try {
      const baseUrl = window.location.origin;
      const pollUrl = createdPollSlug
        ? `${baseUrl}/poll/${createdPollSlug}/vote`
        : `${baseUrl}/poll/${state.pollTitle.replace(/\s+/g, "-").toLowerCase() || "nouveau-sondage"}/vote`;

      await navigator.clipboard.writeText(pollUrl);
      setState((prev) => ({ ...prev, pollLinkCopied: true }));

      // Reset après 3 secondes
      setTimeout(() => {
        setState((prev) => ({ ...prev, pollLinkCopied: false }));
      }, 3000);
    } catch (err) {
      logger.error("Erreur lors de la copie", "poll", err);
    }
  };

  // Fonction pour rediriger vers le dashboard
  const handleBackToHome = () => {
    // Get conversation ID from URL parameters (passed from GeminiChatInterface)
    const urlParams = new URLSearchParams(window.location.search);
    const conversationId = urlParams.get("conversationId");

    if (conversationId) {
      navigate(`/dashboard?resume=${conversationId}`);
    } else {
      navigate("/dashboard");
    }
  };

  // Fonction pour gérer le clic sur le bouton principal
  const handleMainButtonClick = () => {
    logger.debug("Clic bouton principal", "poll", {
      createdPollSlug,
      canFinalize: PollCreatorService.canFinalize(state),
      pollLoading,
      label: pollLoading
        ? "Création en cours..."
        : createdPollSlug
          ? "Sondage créé !"
          : Array.isArray(state.participantEmails)
            ? state.participantEmails.length > 0
              ? "Partager"
              : "Enregistrer"
            : state.participantEmails.trim()
              ? "Partager"
              : "Enregistrer",
    });
    if (createdPollSlug) {
      // Si le sondage est créé, rediriger vers le dashboard
      logger.debug("Bouton après création: redirection dashboard", "poll");
      handleBackToHome();
    } else if (enablePrePublishModal) {
      // Sinon, ouvrir le récap pré-publication (Pattern B)
      logger.debug("Ouverture PrePublishModal", "poll");
      setShowPrePublishModal(true);
    } else {
      // Sinon (flag OFF), créer le sondage directement
      logger.debug("Lancement de handleFinalize (création)", "poll");
      handleFinalize();
    }
  };

  /** Appelé par le PrePublishModal quand l'utilisateur clique "Publier maintenant" */
  const handleConfirmPublish = () => {
    setShowPrePublishModal(false);
    handleFinalize();
  };

  return (
    <>
      {/* Loader pendant le chargement des données d'édition */}
      {isLoadingEdit && editPollId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-gray-700 dark:text-gray-300">
              {t("creator:loading_poll")}
            </p>
          </div>
        </div>
      )}

      <div className="w-[95%] sm:w-[90%] mx-auto space-y-6 pt-2 pb-24">
        {/* Titre du sondage - En haut */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            {t("creator:poll_title_label")}{" "}
            <span className="text-red-400 text-sm">*</span>
          </label>
          <input
            type="text"
            maxLength={140}
            value={state.pollTitle}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                pollTitle: e.target.value.slice(0, 140),
              }))
            }
            className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base text-white placeholder-gray-500"
            placeholder={t("creator:poll_title_placeholder")}
            data-testid="poll-title"
            required
          />
        </div>

        <div className="w-full overflow-hidden">
          <Calendar
            ref={calendarRef}
            visibleMonths={visibleMonths}
            selectedDates={state.selectedDates}
            editMode={!!editPollId}
            onDateToggle={(date: Date) => {
              // Convertir en string YYYY-MM-DD en heure locale (pas UTC)
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              const dateStr = `${year}-${month}-${day}`;
              toggleDate(dateStr);
            }}
            onMonthChange={(direction) => {
              if (direction === "prev") {
                const prevMonth = new Date(visibleMonths[0]);
                prevMonth.setMonth(prevMonth.getMonth() - 1);
                // Ajouter un mois précédent et garder jusqu'à 23 mois
                setVisibleMonths([prevMonth, ...visibleMonths.slice(0, 23)]);
              } else {
                const nextMonth = new Date(
                  visibleMonths[visibleMonths.length - 1],
                );
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                // Ajouter un mois suivant et garder jusqu'à 23 mois
                const twoYearsFromNow = new Date();
                twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
                if (nextMonth <= twoYearsFromNow) {
                  setVisibleMonths([...visibleMonths.slice(1), nextMonth]);
                }
              }
            }}
            onMonthsChange={setVisibleMonths}
          />
        </div>

        {state.selectedDates.length > 0 && (
          <div className="space-y-4">
            {state.showCalendarConnect && !state.calendarConnected && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => {
                      // Sauvegarder l'état actuel avant redirection
                      localStorage.setItem("doodates-return-to", "create");
                      localStorage.setItem("doodates-connect-calendar", "true");
                      localStorage.setItem(
                        "doodates-poll-draft",
                        JSON.stringify({
                          title: state.pollTitle,
                          selectedDates: state.selectedDates,
                          timeSlotsByDate: timeSlotsByDate,
                          participantEmails: state.participantEmails,
                        }),
                      );
                      // Rediriger vers la page de connexion avec intention calendrier
                      window.location.href = "/auth?connect=calendar";
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 underline bg-transparent border-none cursor-pointer"
                  >
                    {t("creator:connect_calendar_optional")}
                  </button>
                  <button
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        showCalendarConnect: false,
                      }))
                    }
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {state.calendarConnected && (
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {t("creator:calendar_connected")}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  {state.selectedDates.length > 0
                    ? t("creator:analyze_availability_hint")
                    : t("creator:select_dates_to_analyze")}
                </p>
              </div>
            )}

            {/* Bouton Horaires - Masqué si les dates forment des groupes */}
            {(() => {
              const dateGroups = groupConsecutiveDates(state.selectedDates);
              const hasGroupedDates = dateGroups.some(
                (group) => group.dates.length > 1,
              );

              // Ne pas afficher le bouton si les dates sont groupées
              if (hasGroupedDates) {
                return null;
              }

              return (
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <button
                    onClick={() => {
                      logger.debug(
                        "Bouton Horaires cliqué - toggle showTimeSlots",
                        "poll",
                      );
                      setState((prev) => {
                        const newState = {
                          ...prev,
                          showTimeSlots: !prev.showTimeSlots,
                        };
                        logger.debug("État après clic", "poll", {
                          selectedDates: prev.selectedDates.length,
                          showTimeSlots: newState.showTimeSlots,
                          conditionMet:
                            prev.selectedDates.length > 0 &&
                            newState.showTimeSlots,
                        });
                        return newState;
                      });
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base border border-gray-300 rounded-lg hover:border-blue-300 transition-colors whitespace-nowrap"
                    data-testid="add-time-slots-button"
                  >
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("creator:time_slots_button")}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        state.showTimeSlots ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <GranularitySelector
                    timeGranularity={state.timeGranularity}
                    timeSlots={state.timeSlots}
                    timeSlotsByDate={timeSlotsByDate}
                    showGranularitySettings={state.showGranularitySettings}
                    onGranularityChange={(value) =>
                      PollCreatorService.handleGranularityChange(
                        value,
                        setState,
                      )
                    }
                    onToggleSettings={() =>
                      setState((prev) => ({
                        ...prev,
                        showGranularitySettings: !prev.showGranularitySettings,
                      }))
                    }
                    onUndoChange={() =>
                      PollCreatorService.undoGranularityChange(setState)
                    }
                    className=""
                  />

                  {/* Bouton Mode fusion */}
                  <button
                    onClick={() => {
                      const newMergeMode = !state.mergeMode;
                      console.log(
                        "🔄 [POLL_CREATOR] Merge button clicked:",
                        `oldMode=${state.mergeMode} newMode=${newMergeMode} buttonText=${newMergeMode ? "Ne pas fusionner" : "Fusionner"}`,
                      );
                      setState((prev) => ({
                        ...prev,
                        mergeMode: newMergeMode,
                      }));
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base border border-gray-300 rounded-lg hover:border-blue-300 transition-colors whitespace-nowrap"
                    title={
                      state.mergeMode
                        ? t("creator:merge_mode.disable_tooltip")
                        : t("creator:merge_mode.enable_tooltip")
                    }
                  >
                    {(() => {
                      console.log(
                        "🔘 [POLL_CREATOR] Merge button state:",
                        `mergeMode=${state.mergeMode} buttonText=${state.mergeMode ? "🔗 Fusionner" : "⚡ Ne pas fusionner"}`,
                      );
                      return state.mergeMode
                        ? t("creator:merge_mode.merging")
                        : t("creator:merge_mode.not_merging");
                    })()}
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Section horaires - Masquée si les dates forment des groupes (week-ends, semaines, quinzaines) */}
        {(() => {
          // Détecter si les dates sélectionnées forment des groupes
          const dateGroups = groupConsecutiveDates(state.selectedDates);
          const hasGroupedDates = dateGroups.some(
            (group) => group.dates.length > 1,
          );

          // Si des dates sont groupées, ne pas afficher la section horaires
          if (hasGroupedDates) {
            return null;
          }

          // Sinon, afficher normalement si conditions remplies
          return state.selectedDates.length > 0 && state.showTimeSlots ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {/* <Clock className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Horaires</h3> */}
              </div>

              {/* Section horaires avec scroll - Responsive */}
              <div data-testid="time-slots-section">
                <div
                  className="
                border border-gray-700 
                rounded-lg 
                bg-[#1e1e1e] 
                overflow-y-auto // Scroll vertical uniquement si le contenu dépasse en hauteur
               "
                >
                  <TimeSlotsTable
                    ref={unifiedTableRef}
                    selectedDates={state.selectedDates}
                    timeSlotsByDate={timeSlotsByDate}
                    compactDateHeaders={compactDateHeaders}
                    timeGranularity={state.timeGranularity}
                    showExtendedHours={state.showExtendedHours}
                    mergeMode={state.mergeMode}
                    onAddTimeSlot={(date, hour, minute) =>
                      handleTimeSlotToggle(date, hour, minute)
                    }
                    onRemoveTimeSlot={(date, hour, minute) =>
                      handleTimeSlotToggle(date, hour, minute)
                    }
                    onToggleExtendedHours={() =>
                      setState((prev) => ({
                        ...prev,
                        showExtendedHours: !prev.showExtendedHours,
                      }))
                    }
                    getTimeSlotBlocks={getTimeSlotBlocks}
                    getVisibleTimeSlots={getVisibleTimeSlots}
                    className=""
                  />
                </div>
              </div>
            </div>
          ) : null;
        })()}

        {/* Sondage récurrent (SOLO) — visible uniquement en création, pas en édition */}
        {!editPollId && (
          <RecurrenceSection
            value={recurrence}
            onChange={setRecurrence}
            hasPaidPlan={hasPaidPlan}
            isAuthenticated={!!user}
            selectedDatesCount={state.selectedDates.length}
          />
        )}

        {/* Configuration avancée — masquée si pre-publish modal actif (settings dans le modal) */}
        {!enablePrePublishModal && (
          <AdvancedConfigurationPanel
            showAdvanced={showAdvanced}
            advancedSettings={advancedSettings}
            onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
            onSettingsChange={setAdvancedSettings}
            pollType="date"
            pollError={pollError}
            emailErrors={state.emailErrors}
          />
        )}

        {/* Boutons d'action - 2 boutons horizontaux */}
        <div className="flex gap-3">
          {/* Bouton Enregistrer */}
          <button
            onClick={() => {
              // Logique pour enregistrer le brouillon dans sessionStorage
              try {
                // Convertir l'état actuel en PollData
                const pollData = PollCreatorService.convertToPollData(state);

                // Sauvegarder dans sessionStorage pour restauration après connexion
                PendingPollService.savePendingPoll({
                  pollData,
                  timestamp: Date.now(),
                  returnPath: window.location.pathname,
                  pollType: "date",
                });

                toast({
                  title: t("creator:draft_saved.title"),
                  description: t("creator:draft_saved.description"),
                });
              } catch (error) {
                console.error(
                  "Erreur lors de la sauvegarde du brouillon:",
                  error,
                );
                toast({
                  title: t("errors:generic"),
                  description: t("creator:draft_save_error_description"),
                  variant: "destructive",
                });
              }
            }}
            disabled={!canFinalize() || pollLoading}
            className="flex-1 px-6 py-3 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-draft-button"
          >
            {t("creator:save_draft_button")}
          </button>

          {/* Bouton Finaliser */}
          <button
            onClick={handleMainButtonClick}
            data-testid="poll-creator-main-button"
            disabled={createdPollSlug ? false : !canFinalize() || isSubmitting}
            className={`flex-1 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              createdPollSlug ? "bg-blue-600 hover:bg-blue-700" : ""
            }`}
          >
            <span>
              {isSubmitting
                ? t("creator:creating_in_progress")
                : createdPollSlug
                  ? t("creator:poll_created_button")
                  : t("creator:finalize_creation_button")}
            </span>
          </button>
        </div>
      </div>

      {/* Modal de restauration des sondages en attente */}
      {hasPendingPoll && pendingPoll && (
        <PendingPollRestoreModal
          isOpen={hasPendingPoll}
          pendingPoll={pendingPoll}
          onRestore={restorePoll}
          onDismiss={dismissPoll}
          isRestoring={isRestoring}
        />
      )}

      {/* Récapitulatif pré-publication (Pattern B, gated par feature flag) */}
      {enablePrePublishModal && (
        <PrePublishModalContainer
          open={showPrePublishModal}
          onOpenChange={setShowPrePublishModal}
          onPublish={handleConfirmPublish}
          onCancel={() => setShowPrePublishModal(false)}
          onApplySuggestion={applyPrePublishSuggestion}
          onEdit={applyPrePublishSuggestion}
          title={state.pollTitle ?? ""}
          selectedDatesCount={
            Array.isArray(state.selectedDates) ? state.selectedDates.length : 0
          }
          timeSlotsCount={
            state.timeSlotsByDate
              ? Object.values(
                  state.timeSlotsByDate as Record<string, unknown[]>,
                ).reduce(
                  (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
                  0,
                )
              : Array.isArray(state.selectedDates)
                ? state.selectedDates.length
                : 0
          }
          participantEmailsCount={
            Array.isArray(state.participantEmails)
              ? state.participantEmails.length
              : typeof state.participantEmails === "string"
                ? state.participantEmails
                    .split(/[,;\n]+/)
                    .map((s) => s.trim())
                    .filter(Boolean).length
                : 0
          }
          firstSelectedDate={
            Array.isArray(state.selectedDates) && state.selectedDates.length > 0
              ? [...state.selectedDates].sort()[0]
              : null
          }
          advancedSettings={advancedSettings}
        />
      )}
    </>
  );
};

export default PollCreator;
